import { CustomReactive, IUpdateCallback } from '@/util';
import { shallowReactive, ref, Ref } from 'vue';
import * as bounds from 'binary-search-bounds';

import * as protocols from '@/protocols';
import { DamageCollectorManager } from '@/actionCollector';
import { optimizationMode, skillClasses } from '@/store';

export interface AggregatedSkillStats {
    id: number; // SkillId
    total: number; // Sum of Damage
    count: number; // Count of hits
    min: number;
    max: number;
    critCount: number;
    // Hits that can crit (delayed ticks never do) - the crit-rate denominator.
    critableCount: number;
}

export interface AggregatedTargetStats {
    targetId: string;
    totalDamage: number;
    skills: Record<number, AggregatedSkillStats>;
    // The player's pets' skills, kept apart from their own (see isOwnerSummon).
    petSkills?: Record<number, AggregatedSkillStats>;
    // Timestamps for accurate DPS calculation
    firstAttackAt: number;
    lastAttackAt: number;
    timeline: Record<number, number>; // Second -> Damage
}

/**
 * isOwnerSummon: marionettes and other summons (bare-number names, summon race
 * block 990xxx) act with their owner's own skills; pets (490xxx) and partners
 * (730xxx) are named and their skills are kept apart. Mirrors the backend's
 * isOwnerSummon in cmd/dilmeterapi/petSkills.go.
 */
export function isOwnerSummon(name: string, raceId: number): boolean {
    return (raceId >= 990000 && raceId < 991000) || /^\d+$/.test(name);
}

// TODO: take cc, apply cc 구분하기

export class ActorManager {
    constructor(private _damageCollector: DamageCollectorManager) {
    }

    public entityMap: Record<string, EntityActor> = shallowReactive({});
    public groupMap: Record<string, GroupActor> = shallowReactive({});
    public damages: protocols.eventDamage[] = [];

    // Refinement 104: Global Combat Intervals (Ingestion-Time)
    public combatIntervals = shallowReactive<{ start: number, end: number }[]>([]);
    private _combatStart = -1;
    private _combatLast = -1;
    private static COMBAT_GAP_THRESHOLD = 3.0;

    // Refinement 109: Reactive trigger for live updates
    public liveUpdate: Ref<number> = ref(0);
    private _lastTrigger = 0;

    public static pcRaceSet = new Set<number>([8001, 8002, 9001, 9002, 10001, 10002]);

    public onEvent(event: protocols.eventBase) {
        if (event.EventId === protocols.eventIdEntityAppear) {
            this.onEntityAppear(event as protocols.eventEntityAppear);
        } else if (event.EventId === protocols.eventIdEntityDisappear) {
            this.onEntityDisappear(event);
        }

        let entity = this.entityMap[event.Id];

        // Refinement 143: Ensure entity exists for state-changing events
        if (!entity && [
            protocols.eventIdCharacterConditionEnable,
            protocols.eventIdCharacterConditionDisable,
            protocols.eventIdEntityEquipItem,
            protocols.eventIdEntityUnequipItem,
            protocols.eventIdEntityUpdateBody
        ].includes(event.EventId)) {
            this.onEntityAppear({
                Id: event.Id,
                EventId: protocols.eventIdEntityAppear,
                At: event.At,
                Name: `unknown:${event.Id}`,
                RaceId: 0,
                Height: 1,
                Weight: 1,
                Upper: 1,
                Lower: 1,
                GuildName: "",
                OwnerId: "",
            });
            entity = this.entityMap[event.Id];
        }

        switch (event.EventId) {
            case protocols.eventIdEntityAppear:
                // TODO: entityAppear에서 master id, hp 가져오기, damage, cc 처리할 때 master id가 있으면 그쪽으로 보내야함
                entity?.onEntityAppear(event as protocols.eventEntityAppear);
                break;

            case protocols.eventIdDamage:
                {
                    const event_ = event as protocols.eventDamage;
                    this.damages.push(event_);

                    // Refinement 104: Track Combat Intervals based on Player Outgoing Damage
                    const now = event_.At; // Assuming At is seconds (float)

                    // Live Update Trigger (Throttle ~1s) to force reactivity in components
                    const nowMs = Date.now();
                    if (nowMs - this._lastTrigger > 1000) {
                        this.liveUpdate.value++;
                        this._lastTrigger = nowMs;
                    }

                    if (entity && entity.isPC) {

                        if (this._combatStart === -1) {
                            this._combatStart = now;
                            this._combatLast = now;
                        } else {
                            const gap = now - this._combatLast;
                            if (gap > ActorManager.COMBAT_GAP_THRESHOLD) {
                                // Close previous interval
                                this.combatIntervals.push({ start: this._combatStart, end: this._combatLast });
                                // Start new
                                this._combatStart = now;
                            }
                            this._combatLast = now;
                        }
                    }

                    let actualAttacker = entity;
                    if (entity && !entity.isPC && entity.ownerId && entity.ownerId !== "0" && entity.ownerId !== 0 as any) {
                        let owner = this.entityMap[entity.ownerId];
                        // Only proxy swap if the organic owner successfully spawned and is a valid player
                        if (owner && owner.isPC) {
                            actualAttacker = owner;
                        }
                    }

                    if (actualAttacker) {
                        actualAttacker.onApplyDamage(event_);
                        actualAttacker.group.onApplyDamage(event_);
                    }

                    let targetEntity = this.entityMap[event_.TargetId];
                    if (!targetEntity) {
                        // 유저 정보가 오기전에 대미지가 먼저오는 경우
                        // @TODO: 추후에 local storage를 사용해 캐싱하는 식으로 변경
                        this.onEntityAppear({
                            Id: event_.TargetId,
                            EventId: protocols.eventIdEntityAppear,
                            At: Date.now() / 1000,
                            Name: `unknown:${event_.TargetId}`,
                            RaceId: 10001,
                            Height: 1,
                            Weight: 1,
                            Upper: 1,
                            Lower: 1,
                            GuildName: "",
                            OwnerId: "",
                        })
                        targetEntity = this.entityMap[event_.TargetId];
                    }

                    targetEntity.onTakeDamage(event_);
                    targetEntity.group.onTakeDamage(event_);
                }
                break;

            case protocols.eventIdHealing:
                {
                    const event_ = event as protocols.eventHealing;
                    if (entity) {
                        entity.onApplyHealing(event_);
                        entity.group.onApplyHealing(event_);
                    }

                    let targetEntity = this.entityMap[event_.TargetId];
                    if (!targetEntity) {
                        this.onEntityAppear({
                            Id: event_.TargetId,
                            EventId: protocols.eventIdEntityAppear,
                            At: Date.now() / 1000,
                            Name: `unknown:${event_.TargetId}`,
                            RaceId: 10001,
                            Height: 1,
                            Weight: 1,
                            Upper: 1,
                            Lower: 1,
                            GuildName: "",
                            OwnerId: "",
                        })
                        targetEntity = this.entityMap[event_.TargetId];
                    }

                    targetEntity.onTakeHealing(event_);
                    targetEntity.group.onTakeHealing(event_);
                }
                break;

            case protocols.eventIdCharacterConditionEnable:
                entity?.onCharacterConditionEnable(event as protocols.eventCharacterConditionEnable);
                // Trigger live update so downstream computeds (playerStats, raidMusicalBuffs)
                // re-evaluate with the new condition data. Use the same throttle as damage events.
                {
                    const nowMs = Date.now();
                    if (nowMs - this._lastTrigger > 1000) {
                        this.liveUpdate.value++;
                        this._lastTrigger = nowMs;
                    }
                }
                break;

            case protocols.eventIdCharacterConditionDisable:
                entity?.onCharacterConditionDisable(event as protocols.eventCharacterConditionDisable);
                {
                    const nowMs = Date.now();
                    if (nowMs - this._lastTrigger > 1000) {
                        this.liveUpdate.value++;
                        this._lastTrigger = nowMs;
                    }
                }
                break;

            case protocols.eventIdFinish:
                entity?.onFinish(event as protocols.eventFinish);
                break;

            case protocols.eventIdEntityEquipItem:
                entity?.onEquipItem(event as protocols.eventEntityEquipItem);
                break;

            case protocols.eventIdEntityUnequipItem:
                entity?.onUnequipItem(event as protocols.eventEntityUnequipItem);
                break;

            case protocols.eventIdEntityUpdateBody:
                entity?.onUpdateBody(event as protocols.eventEntityUpdateBody);
                break;

            case protocols.eventIdEntityHPUpdate: {
                const hpEvent = event as protocols.eventEntityHPUpdate;
                if (!entity) {
                    // Create a lightweight placeholder so HP is visible before an EntityAppear arrives
                    this.onEntityAppear({
                        Id: hpEvent.Id, EventId: protocols.eventIdEntityAppear, At: hpEvent.At,
                        Name: `unknown:${hpEvent.Id}`, RaceId: 0,
                        Height: 1, Weight: 1, Upper: 1, Lower: 1, GuildName: '', OwnerId: '',
                    });
                    entity = this.entityMap[hpEvent.Id];
                }
                if (entity instanceof EntityActor) {
                    entity.onHPUpdate(hpEvent);
                }
                break;
            }
        }
    }

    public onEntityAppear(event: protocols.eventEntityAppear) {
        const { Id, RaceId, Name } = event;

        const groupKey = ActorManager.groupTargetKey(event);
        const group = this.groupMap[groupKey] ??= CustomReactive(new GroupActor(this, groupKey, RaceId, Name));

        let entity = this.entityMap[Id];
        const isNewEntity = !entity;
        const dummyEntity = entity?.name.startsWith('unknown:');

        if (isNewEntity || dummyEntity) {
            if (isNewEntity) {
                entity = CustomReactive(new EntityActor(this, Id, RaceId, Name, group));
                this.entityMap[Id] = group.entityMap[Id] = entity;
            } else if (dummyEntity) {
                // Update dummy entity with real data
                (entity as any)._name = Name;
                (entity as any)._raceId = RaceId;
                (entity as any)._isPC = ActorManager.pcRaceSet.has(RaceId);
                entity.onEntityAppear(event);
            }

            // Refinement 155: Removed O(N*M) look-back loop that scanned all historical damages.
            // This was causing a massive UI freeze during log hydration.
            // For live capture, any "pre-received" damage is now handled by the backend
            // or by standard event stream order.
        }
    }

    public onEntityDisappear(event: protocols.eventBase) {
        const entity = this.entityMap[event.Id];
        if (entity) {
            entity.onEntityDisappear(event);
        }
    }

    public onEntityDamage(event: EntityDamage) {
        this._damageCollector.onDamage(event);
    }

    public getAllCombatIntervals(): { start: number, end: number }[] {
        // HACK: Dependency on liveUpdate to force re-evaluation during active combat
        this.liveUpdate.value;

        const intervals = [...this.combatIntervals];
        if (this._combatStart !== -1) {
            intervals.push({ start: this._combatStart, end: this._combatLast });
        }
        return intervals;
    }

    public clear() {
        // PERFORMANCE: Delete all keys from maps instead of iterating to call .clear()
        // This preserves the reactive map references that components depend on
        this.damages.length = 0;

        // Delete all entity keys
        for (const k of Object.keys(this.entityMap)) {
            delete this.entityMap[k];
        }

        // Delete all group keys
        for (const k of Object.keys(this.groupMap)) {
            delete this.groupMap[k];
        }

        // Reset Combat Intervals
        this.combatIntervals.length = 0;
        this._combatStart = -1;
        this._combatLast = -1;
        this.liveUpdate.value = 0;
        this._lastTrigger = 0;
    }

    private static groupTargetKey(event: protocols.eventEntityAppear): string {
        // pc 일 경우 group안에 entity가 여러개 생기지 않도록
        if (this.pcRaceSet.has(event.RaceId)) {
            return event.Id;
        }

        return `${event.RaceId}`;
    }
}

interface IEventActor {
    /** damage 쪽 수치들만 reset */
    clear(): void;
}

export abstract class BaseActor implements IEventActor, IUpdateCallback {
    protected vueUpdateTrack?: () => void;
    private vueUpdateTrigger?: () => void;
    private vueUpdateTimeout = 0;
    private static vueUpdateTick = 33;

    protected constructor(protected mgr: ActorManager, private _id: string, protected _raceId: number, protected _name: string) {
    }

    public get id() {
        this.vueUpdateTrack?.();
        return this._id;
    }

    public get raceId() {
        this.vueUpdateTrack?.();
        return this._raceId;
    }

    public get name() {
        this.vueUpdateTrack?.();
        return this._name;
    }

    protected _body: EntityBody = {
        Height: 1,
        Weight: 1,
        Upper: 1,
        Lower: 1,
    };
    public get body() {
        this.vueUpdateTrack?.();
        return this._body;
    }

    /** 받은 대미지 */
    public get totalTakeDamage() {
        this.vueUpdateTrack?.();
        return this._totalTakeDamage;
    }
    protected _totalTakeDamage = 0;

    protected _takeDamages: EntityDamage[] = [];
    public get takeDamages() {
        this.vueUpdateTrack?.();
        return this._takeDamages;
    }

    /** 준 대미지 */
    public get totalApplyDamage() {
        this.vueUpdateTrack?.();
        return this._totalApplyDamage;
    }
    protected _totalApplyDamage = 0;

    protected _applyDamages: EntityDamage[] = [];
    public get applyDamages() {
        this.vueUpdateTrack?.();
        return this._applyDamages;
    }

    /** 준 힐링 (Healing Done) */
    public get totalApplyHealing() {
        this.vueUpdateTrack?.();
        return this._totalApplyHealing;
    }
    protected _totalApplyHealing = 0;

    /** 받은 힐링 (Healing Received) */
    public get totalTakeHealing() {
        this.vueUpdateTrack?.();
        return this._totalTakeHealing;
    }
    protected _totalTakeHealing = 0;

    public get isPC() {
        this.vueUpdateTrack?.();
        return ActorManager.pcRaceSet.has(this._raceId);
    }

    protected _disappearAt = 0;
    public get disappearAt() {
        this.vueUpdateTrack?.();
        return this._disappearAt;
    }

    public onEntityAppear(event: protocols.eventEntityAppear): void {
        // nothing
        event;
    }

    public onEntityDisappear(event: protocols.eventBase): void {
        this._disappearAt = event.At;
        this.vueUpdateRequest();
    }

    public onTakeDamage(event: protocols.eventDamage): void {
        // nothing
        event;
    }

    public onApplyDamage(event: protocols.eventDamage): void {
        // nothing
        event;
    }

    public onApplyHealing(event: protocols.eventHealing): void {
        this._totalApplyHealing += event.Healing;
        this.vueUpdateRequest();
    }

    public onTakeHealing(event: protocols.eventHealing): void {
        this._totalTakeHealing += event.Healing;
        this.vueUpdateRequest();
    }

    public onCharacterConditionEnable(event: protocols.eventCharacterConditionEnable): void {
        // nothing
        event;
    }

    public onCharacterConditionDisable(event: protocols.eventCharacterConditionDisable): void {
        // nothing
        event;
    }

    public onFinish(event: protocols.eventFinish): void {
        // nothing
        event;
    }

    public onEquipItem(event: protocols.eventEntityEquipItem): void {
        // nothing
        event;
    }

    public onUnequipItem(event: protocols.eventEntityUnequipItem): void {
        // nothing
        event;
    }

    public onUpdateBody(event: protocols.eventEntityUpdateBody): void {
        this._body.Height = event.Height;
        this._body.Weight = event.Weight;
        this._body.Upper = event.Upper;
        this._body.Lower = event.Lower;

        this.vueUpdateRequest();
    }

    public clear() {
        this._disappearAt = 0;
        this._totalTakeDamage = 0;
        this._takeDamages.length = 0;

        this._totalApplyDamage = 0;
        this._applyDamages.length = 0;

        this._totalApplyHealing = 0;
        this._totalTakeHealing = 0;

        this.vueUpdateRequest();
    }

    public setUpdateCallback(track: () => void, trigger: () => void): void {
        this.vueUpdateTrack = track;
        this.vueUpdateTrigger = trigger;
    }

    private vueUpdate(): void {
        this.vueUpdateTimeout = 0;
        this.vueUpdateTrigger?.();
    }

    protected vueUpdateRequest(): void {
        if (this.vueUpdateTimeout) {
            return;
        }

        this.vueUpdateTimeout = setTimeout(() => this.vueUpdate(), BaseActor.vueUpdateTick);
    }
}

export class EntityActor extends BaseActor {
    public constructor(mgr: ActorManager, id: string, raceId: number, name: string, private _group: GroupActor) {
        super(mgr, id, raceId, name);
    }

    protected _guildName = '';
    public get guildName() {
        this.vueUpdateTrack?.();
        return this._guildName;
    }

    protected _lastTakeDamageAt = 0;
    public get lastTakeDamageAt() {
        this.vueUpdateTrack?.();
        return this._lastTakeDamageAt;
    }

    protected _ownerId = '';
    public get ownerId() {
        this.vueUpdateTrack?.();
        return this._ownerId;
    }

    public get group() {
        this.vueUpdateTrack?.();
        return this._group;
    }

    protected _conditionMap: Record<number, EntityCondition> = {};
    public get conditionMap() {
        this.vueUpdateTrack?.();
        return this._conditionMap;
    }

    protected _conditionHistory: EntityConditionState[] = [];
    public get conditionHistory() {
        return this._conditionHistory;
    }

    private _finisherId = '';
    public get finisherId() {
        this.vueUpdateTrack?.();
        return this._finisherId;
    }

    protected _equipItemMap: Record<number, EntityItem> = {};
    public get equipItemMap() {
        this.vueUpdateTrack?.();
        return this._equipItemMap;
    }

    // Refinement 123: Aggregated Stats
    public aggregatedStats: Record<string, AggregatedTargetStats> = {};
    public aggregatedTakeStats: Record<string, AggregatedTargetStats> = {};

    // Refinement 133: Weapon Usage by Class (format: { ClassName: { "weaponId" or "weaponId_leftId": count } })
    public weaponUsageByClass: Record<string, Record<string, number>> = {};

    // Incremental Status Timeline
    // Incremental Status Timeline
    public statusTimelines: Record<number, { start: number, end: number, max?: number, magicAttack?: number, alchemicDamage?: number, magicCastingSpeed?: number, alchemyCastingSpeed?: number }[]> = {};
    private _statusStarts: Record<number, { time: number, max?: number, magicAttack?: number, alchemicDamage?: number, magicCastingSpeed?: number, alchemyCastingSpeed?: number }> = {};

    // HP tracking (live, updated by opcodePublicStatUpdate events)
    protected _currentHP = 0;
    protected _maxHP = 0;
    public get currentHP() { this.vueUpdateTrack?.(); return this._currentHP; }
    public get maxHP()     { this.vueUpdateTrack?.(); return this._maxHP; }

    // HP-over-time history for the Analysis tab's DPS graph overlay.
    // Populated from the backend's per-entity hpHistory (see App.vue mergeEntity),
    // which the CombatManager accumulates for both live sync and saved-session replay.
    public hpHistory: { time: number, currentHp: number, maxHp: number }[] = [];

    // Spawn timestamp for boss/ad sort
    protected _appearAt = 0;
    public get appearAt() {
        this.vueUpdateTrack?.();
        return this._appearAt;
    }

    public override onEntityAppear(event: protocols.eventEntityAppear): void {
        this.vueUpdateRequest();

        if (this._appearAt === 0) {
            this._appearAt = event.At;
        }

        this._name = event.Name;
        this._raceId = event.RaceId;
        this._finisherId = '';
        this._guildName = event.GuildName;
        this._ownerId = event.OwnerId;
        this._body.Height = event.Height;
        this._body.Weight = event.Weight;
        this._body.Upper = event.Upper;
        this._body.Lower = event.Lower;

        if (ActorManager.pcRaceSet.has(event.RaceId)) {
            // pc일 경우 damage 초기와 안함
            return;
        }

        this._totalTakeDamage = 0;
        this._takeDamages.length = 0;

        // Reset timelines for monsters on respawn/appear.
        // Before wiping, close any open status intervals that were tracked on the
        // previous lifetime (including those captured from a pre-existing dummy entity).
        // This prevents conditions visible in damageList from showing 0% coverage.
        for (const [ccIdStr, entry] of Object.entries(this._statusStarts)) {
            const ccId = +ccIdStr;
            if (!this.statusTimelines[ccId]) this.statusTimelines[ccId] = [];
            this.statusTimelines[ccId].push({ start: entry.time, end: event.At, max: entry.max });
        }
        this.statusTimelines = {};
        this._statusStarts = {};

        // Keep conditionMap and conditionHistory in sync with the reset above
        // so damageList never shows stale conditions after a respawn.
        this._conditionMap = {};
        this._conditionHistory = [];
        this.aggregatedStats = {};
        this.aggregatedTakeStats = {};
    }


    public override onTakeDamage(event: protocols.eventDamage): void {
        this.vueUpdateRequest();

        const attacker = this.mgr.entityMap[event.Id];

        const damage: EntityDamage = {
            ...event,

            Conditions: attacker?.getConditionState(event.At) ?? [],
            TargetConditions: this.getConditionState(event.At),
            PetId: '',
        }

        this._totalTakeDamage += event.Damage;
        this._takeDamages.push(damage);
        this._lastTakeDamageAt = event.At;
    }

    public override onApplyDamage(event: protocols.eventDamage): void {
        this.vueUpdateRequest();

        const targetId = event.TargetId;
        const target = this.mgr.entityMap[targetId];
        if (!target || !(target instanceof EntityActor)) {
            // 몹 정보가 없으면 무시
            return;
        }
        const attackerId = event.Id;
        const attacker = this.mgr.entityMap[attackerId];

        const damage: EntityDamage = {
            ...event,

            Conditions: this.getConditionState(event.At),
            TargetConditions: target.getConditionState(event.At),
            PetId: '',
        }

        // apply의 경우 pet 대신 pc가 공격한 것 처럼 처리
        if (attacker?.ownerId && !attacker.isPC && attacker.ownerId !== "0" && attacker.ownerId !== 0 as any) {
            damage.PetId = attackerId;
            damage.Id = attacker.ownerId;
            damage.FromPet = !isOwnerSummon(attacker.name, attacker.raceId);
        }

        // Refinement 123: Ingestion-Time Aggregation
        this.aggregateDamage(damage);

        this._totalApplyDamage += event.Damage;

        if (!optimizationMode.value) {
            this._applyDamages.push(damage);
        }

        // apply에서만 호출
        this.mgr.onEntityDamage(damage);
    }

    private aggregateDamage(d: EntityDamage) {
        if (!this.aggregatedStats[d.TargetId]) {
            this.aggregatedStats[d.TargetId] = {
                targetId: d.TargetId,
                totalDamage: 0,
                skills: {},
                firstAttackAt: d.At,
                lastAttackAt: d.At,
                timeline: {},
            };
        }

        const tStats = this.aggregatedStats[d.TargetId];
        tStats.totalDamage += d.Damage;
        if (d.At < tStats.firstAttackAt) tStats.firstAttackAt = d.At;
        if (d.At > tStats.lastAttackAt) tStats.lastAttackAt = d.At;

        // Timeline Bucket (1s resolution)
        const sec = Math.floor(d.At);
        tStats.timeline[sec] = (tStats.timeline[sec] || 0) + d.Damage;

        const skills = d.FromPet ? (tStats.petSkills ??= {}) : tStats.skills;
        if (!skills[d.SkillId]) {
            skills[d.SkillId] = {
                id: d.SkillId,
                total: 0,
                count: 0,
                min: Number.MAX_VALUE,
                max: 0,
                critCount: 0,
                critableCount: 0,
            };
        }

        const sStats = skills[d.SkillId];
        sStats.total += d.Damage;
        sStats.count++;
        if (!d.IsDelayed) sStats.critableCount++;
        if (d.IsCritical) sStats.critCount++;
        if (d.Damage < sStats.min) sStats.min = d.Damage;
        if (d.Damage > sStats.max) sStats.max = d.Damage;

        // Refinement 133: Track weapons by class usage
        // When using a skill, record which weapon(s) were equipped
        if (skillClasses.value.length > 0 && !d.FromPet) {
            let foundClass = false;
            for (const cls of skillClasses.value) {
                if (cls.skills.includes(d.SkillId)) {
                    foundClass = true;
                    // Refinement 142: PocketType mapping
                    // 10 = PRIMARY
                    // 12 = SECONDARY/Shield
                    // 14 = AMMO
                    const primaryWeapon = this._equipItemMap[10];
                    const secondaryWeapon = this._equipItemMap[12];
                    const ammo = this._equipItemMap[14];

                    if (primaryWeapon?.ItemId) {
                        let weaponKey = `${primaryWeapon.ItemId}`;

                        // Check both secondary and ammo
                        if (secondaryWeapon?.ItemId) {
                            weaponKey += `_${secondaryWeapon.ItemId}`;
                        }
                        if (ammo?.ItemId) {
                            weaponKey += `_${ammo.ItemId}`;
                        }

                        if (!this.weaponUsageByClass[cls.name]) {
                            this.weaponUsageByClass[cls.name] = {};
                        }
                        this.weaponUsageByClass[cls.name][weaponKey] =
                            (this.weaponUsageByClass[cls.name][weaponKey] || 0) + 1;
                    }
                    break; // Found the class, no need to continue
                }
            }
        }
    }

    public override onCharacterConditionEnable(event: protocols.eventCharacterConditionEnable): void {
        this.vueUpdateRequest();

        // Incremental Timeline Building
        // If status wasn't already active, mark start time
        if (this._statusStarts[event.CCId] === undefined) {
            this._statusStarts[event.CCId] = { time: event.At };
        }

        let mcmbamax: number | undefined;
        let mcmbamin: number | undefined;
        let mcmbac: number | undefined;
        let magicAttack: number | undefined;
        let alchemicDamage: number | undefined;
        let magicCastingSpeed: number | undefined;
        let alchemyCastingSpeed: number | undefined;

        if (event.Meta) {
            const parts = event.Meta.split(';');
            for (const p of parts) {
                if (p.startsWith('MCMBAMAX:f:')) {
                    mcmbamax = Math.round(parseFloat(p.substring(11)) * 100) / 100;
                } else if (p.startsWith('MCMBAMIN:f:')) {
                    mcmbamin = Math.round(parseFloat(p.substring(11)) * 100) / 100;
                } else if (p.startsWith('MCMBAC:f:')) {
                    mcmbac = Math.round(parseFloat(p.substring(9)) * 100) / 100;
                } else if (p.startsWith('LSMA:f:')) {
                    magicAttack = Math.round(parseFloat(p.substring(7)) * 100) / 100;
                } else if (p.startsWith('MCMLSADR:f:')) {
                    alchemicDamage = Math.round(parseFloat(p.substring(11)) * 10000) / 100; // Convert decimal to %
                } else if (p.startsWith('MFCP:f:')) {
                    magicCastingSpeed = Math.round(parseFloat(p.substring(7)) * 100) / 100;
                } else if (p.startsWith('AFCP:f:')) {
                    alchemyCastingSpeed = Math.round(parseFloat(p.substring(7)) * 100) / 100;
                }
            }
        }

        const startEntry = this._statusStarts[event.CCId];
        if (startEntry) {
            if (mcmbamax !== undefined && (startEntry.max === undefined || mcmbamax > startEntry.max)) {
                startEntry.max = mcmbamax;
            }
            if (magicAttack !== undefined && (startEntry.magicAttack === undefined || magicAttack > startEntry.magicAttack)) {
                startEntry.magicAttack = magicAttack;
            }
            if (alchemicDamage !== undefined && (startEntry.alchemicDamage === undefined || alchemicDamage > startEntry.alchemicDamage)) {
                startEntry.alchemicDamage = alchemicDamage;
            }
            if (magicCastingSpeed !== undefined && (startEntry.magicCastingSpeed === undefined || magicCastingSpeed > startEntry.magicCastingSpeed)) {
                startEntry.magicCastingSpeed = magicCastingSpeed;
            }
            if (alchemyCastingSpeed !== undefined && (startEntry.alchemyCastingSpeed === undefined || alchemyCastingSpeed > startEntry.alchemyCastingSpeed)) {
                startEntry.alchemyCastingSpeed = alchemyCastingSpeed;
            }
        }

        this._conditionMap[event.CCId] = {
            Id: event.Id,
            At: event.At,
            CCId: event.CCId,
            DisableAt: event.DisableAt,
            AttackerId: event.AttackerId,
            Meta: event.Meta,
            mcmbamax,
            mcmbamin,
            mcmbac
        };

        const prev = this._conditionHistory.length ? this._conditionHistory[this._conditionHistory.length - 1].List : [];
        const current = Object.values(this._conditionMap).sort((a, b) => a.CCId - b.CCId);

        const needUpdate = prev.length !== current.length || !prev.every((v, i) => v.CCId === current[i].CCId);
        if (needUpdate) {
            this._conditionHistory.push({
                At: event.At,
                List: current,
            });
        }
    }

    public override onCharacterConditionDisable(event: protocols.eventCharacterConditionDisable): void {
        this.vueUpdateRequest();

        // Incremental Timeline Building
        // Close interval if active
        const entry = this._statusStarts[event.CCId];
        if (entry !== undefined) {
            if (!this.statusTimelines[event.CCId]) {
                this.statusTimelines[event.CCId] = [];
            }
            this.statusTimelines[event.CCId].push({ 
                start: entry.time, end: event.At, max: entry.max,
                magicAttack: entry.magicAttack, alchemicDamage: entry.alchemicDamage,
                magicCastingSpeed: entry.magicCastingSpeed, alchemyCastingSpeed: entry.alchemyCastingSpeed
            });
            delete this._statusStarts[event.CCId];
        }

        delete this._conditionMap[event.CCId];

        const prev = this._conditionHistory.length ? this._conditionHistory[this._conditionHistory.length - 1].List : [];
        const current = Object.values(this._conditionMap).sort((a, b) => a.CCId - b.CCId);

        const needUpdate = prev.length !== current.length || !prev.every((v, i) => v.CCId === current[i].CCId);
        if (needUpdate) {
            this._conditionHistory.push({
                At: event.At,
                List: current,
            });
        }
    }

    public override onFinish(event: protocols.eventFinish): void {
        this.vueUpdateRequest();

        this._finisherId = event.AttackerId;
    }

    public override onEquipItem(event: protocols.eventEntityEquipItem): void {
        this._equipItemMap[event.PocketType] = event;
        this.vueUpdateRequest();
    }

    public override onUnequipItem(event: protocols.eventEntityUnequipItem): void {
        this.vueUpdateRequest();

        delete this._equipItemMap[event.PocketType];
    }

    public onHPUpdate(event: protocols.eventEntityHPUpdate): void {
        this._currentHP = event.currentHp;
        if (event.maxHp > 0) this._maxHP = event.maxHp;
        this.vueUpdateRequest();
    }

    public getConditionState(at: number): EntityCondition[] {
        const idx = bounds.le<{ At: number }>(this._conditionHistory, { At: at }, (a, b) => a.At - b.At);
        if (idx < 0) {
            return [];
        }

        return this._conditionHistory[idx].List;
    }

    public override clear() {
        super.clear();
        this.statusTimelines = {};
        this._statusStarts = {};
        this.aggregatedStats = {};
        this._appearAt = 0;
    }
}

// TODO: GroupActor에 Group 조건 추가하는 식으로 바꾸는게 좋을듯
export class GroupActor extends BaseActor {
    public constructor(mgr: ActorManager, id: string, raceId: number, name: string) {
        const groupName = ActorManager.pcRaceSet.has(raceId)
            ? name : `${raceId}`;

        super(mgr, id, raceId, groupName);
    }

    private _entityMap: Record<string, EntityActor> = {};
    public get entityMap() {
        this.vueUpdateTrack?.();
        return this._entityMap;
    }

    public override onEntityAppear(event: protocols.eventEntityAppear): void {
        this.vueUpdateRequest();

        this._name = ActorManager.pcRaceSet.has(event.RaceId)
            ? event.Name : `${event.RaceId}`;
        this._raceId = event.RaceId;

        if (ActorManager.pcRaceSet.has(event.RaceId)) {
            // pc일 경우 damage 초기와 안함
            return;
        }

        const target = this._entityMap[event.Id];
        if (!target) {
            // ?
            return;
        }

        this._takeDamages = this._takeDamages.filter(v => v.Id !== event.Id);
        this._totalTakeDamage = this._takeDamages.reduce((acc, v) => acc + v.Damage, 0);
    }

    public override onTakeDamage(event: protocols.eventDamage): void {
        this.vueUpdateRequest();
        // console.log('group take damage', this.id, event);

        const attacker = this.mgr.entityMap[event.Id];
        const target = this._entityMap[event.TargetId];
        if (!target) {
            // ?
            return;
        }

        const damage: EntityDamage = {
            ...event,

            Conditions: attacker?.getConditionState(event.At) ?? [],
            TargetConditions: target.getConditionState(event.At),
            PetId: '',
        }

        this._totalTakeDamage += event.Damage;
        this._takeDamages.push(damage);
    }

    public override clear(): void {
        super.clear();
    }
}

export type EntityDamage = {
    Id: string;
    At: number;
    TargetId: string;
    SkillId: number;
    Damage: number;
    IsCritical: boolean;
    IsDelayed: boolean;
    Conditions: EntityCondition[];
    TargetConditions: EntityCondition[];
    PetId: string; // 펫 공격일 경우
    FromPet?: boolean; // a pet (not a marionette) dealt it: its skills stay apart
};

export type EntityCondition = {
    Id: string;
    At: number;
    CCId: number;
    DisableAt: number;
    AttackerId: string;
    Meta?: string;
    mcmbamax?: number;
    mcmbamin?: number;
    mcmbac?: number;
}

type EntityConditionState = {
    At: number;
    List: EntityCondition[];
}

export type EntityItem = {
    PocketType: number;
    ItemId: number;
    Color1: string;
    Color2: string;
    Color3: string;
    Color5: string;
    Color6: string;
    Color7: string;
}

export type EntityBody = {
    Height: number;
    Weight: number;
    Upper: number;
    Lower: number;
}